import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { Product, Brand, Category } from "@/types";
import { Upload, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { invokeAdmin } from "@/services/functions";
import { v4 as uuidv4 } from "uuid";

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingProduct: Product | null;
  brands: Brand[];
  categories: Category[];
  subcategories: Category[];
  onProductSaved: () => Promise<void>;
}

const ProductModal: React.FC<ProductModalProps> = ({
  isOpen,
  onClose,
  editingProduct,
  brands,
  categories,
  subcategories,
  onProductSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [additionalImageFiles, setAdditionalImageFiles] = useState<File[]>([]);
  const [filteredSubcategories, setFilteredSubcategories] = useState<Category[]>([]);
  const [formData, setFormData] = useState<Omit<Product, "id">>({
    name: "",
    description: "",
    image: "",
    price: 0,
    pointsPrice: 0,
    brandId: "",
    categoryId: "",
    subcategoryId: "",
    stock: 0,
    additionalImages: [],
    status: 'active',
  });

  const isEditing = !!editingProduct;

  // Populate form when editing product
  useEffect(() => {
    if (editingProduct) {
      setFormData({
        name: editingProduct.name,
        description: editingProduct.description || "",
        image: editingProduct.image,
        price: editingProduct.price,
        pointsPrice: editingProduct.pointsPrice || 0,
        brandId: editingProduct.brandId || "",
        categoryId: editingProduct.categoryId || "",
        subcategoryId: editingProduct.subcategoryId || "",
        stock: editingProduct.stock || 0,
        additionalImages: editingProduct.additionalImages || [],
        status: editingProduct.status || 'active',
      });

      // Filter subcategories for the selected category
      if (editingProduct.categoryId) {
        const filtered = subcategories.filter(subcat => subcat.parentId === editingProduct.categoryId);
        setFilteredSubcategories(filtered);
      }
    } else {
      // Reset form for new product
      resetForm();
    }
  }, [editingProduct, isOpen, subcategories]);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      image: "",
      price: 0,
      pointsPrice: 0,
      brandId: "",
      categoryId: "",
      subcategoryId: "",
      stock: 0,
      additionalImages: [],
      status: 'active',
    });
    setImageFile(null);
    setAdditionalImageFiles([]);
    setFilteredSubcategories([]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let parsedValue = value;

    if (name === "price" || name === "pointsPrice" || name === "stock") {
      parsedValue = value === "" ? "0" : value;
    }

    setFormData(prev => ({ ...prev, [name]: parsedValue }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));

    // Filter subcategories when category changes
    if (name === "categoryId") {
      const filtered = subcategories.filter(subcat => subcat.parentId === value);
      setFilteredSubcategories(filtered);

      // Reset subcategory if it doesn't belong to the selected category
      const currentSubcatId = formData.subcategoryId;
      const belongsToCategory = filtered.some(subcat => subcat.id === currentSubcatId);

      if (!belongsToCategory) {
        setFormData(prev => ({ ...prev, subcategoryId: "" }));
      }
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      const imageUrl = URL.createObjectURL(file);
      setFormData(prev => ({ ...prev, image: imageUrl }));
      toast({
        title: "Imagem selecionada",
        description: `${file.name} (${Math.round(file.size / 1024)}KB)`,
      });
    }
  };

  const handleAdditionalImagesUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      setAdditionalImageFiles(prev => [...prev, ...fileArray]);
      toast({
        title: "Imagens adicionais selecionadas",
        description: `${fileArray.length} ficheiro(s) seleccionado(s)`,
      });
    }
  };

  const removeAdditionalImage = (index: number) => {
    setAdditionalImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingAdditionalImage = (imageUrl: string) => {
    if (isEditing && formData.additionalImages) {
      const updatedImages = formData.additionalImages.filter(img => img !== imageUrl);
      setFormData(prev => ({ ...prev, additionalImages: updatedImages }));
    }
  };

  const validateForm = () => {
    if (!formData.name) {
      toast({
        title: "Erro",
        description: "Nome do produto é obrigatório",
        variant: "destructive",
      });
      return false;
    }

    if (!formData.brandId) {
      toast({
        title: "Erro",
        description: "Seleccione uma marca",
        variant: "destructive",
      });
      return false;
    }

    if (!formData.categoryId) {
      toast({
        title: "Erro",
        description: "Seleccione uma categoria",
        variant: "destructive",
      });
      return false;
    }

    if (!formData.image && !imageFile) {
      toast({
        title: "Erro",
        description: "Seleccione uma imagem principal para o produto",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const uploadImage = async (file: File, oldImageUrl?: string) => {
    try {
      // Block SVG files
      if (file.type === 'image/svg+xml') {
        throw new Error('SVG não é suportado');
      }

      // Validate file type
      const supportedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
      if (!supportedTypes.includes(file.type)) {
        throw new Error('Tipo de ficheiro não suportado');
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Ficheiro muito grande. Limite de 5MB');
      }

      const fileExt = file.name.split('.').pop();
      if (!fileExt) {
        throw new Error('Ficheiro deve ter uma extensão válida');
      }
      const fileName = `${uuidv4()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('products')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: publicUrl } = supabase.storage
        .from('products')
        .getPublicUrl(fileName);

      // Clean up old image if provided
      if (oldImageUrl && !oldImageUrl.includes('lovable-uploads')) {
        try {
          const urlParts = oldImageUrl.split('/');
          const oldFileName = urlParts.pop();
          if (oldFileName && oldFileName.includes('.')) {
            await supabase.storage.from('products').remove([oldFileName]);
          }
        } catch (cleanupError) {
          console.warn('Failed to cleanup old image:', cleanupError);
        }
      }

      return publicUrl.publicUrl;
    } catch (error: any) {
      toast({
        title: "Erro",
        description: "Erro no carregamento da imagem. Tente novamente.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);

      let imageUrl = formData.image;
      const additionalImagesUrls: string[] = [];

      // Upload main image if it's a file
      if (imageFile) {
        const oldImageUrl = formData.image.startsWith('blob:') ? undefined : formData.image;
        imageUrl = await uploadImage(imageFile, oldImageUrl);
      } else if (formData.image.startsWith('blob:') && isEditing) {
        // Fix blob URL issue for editing
        imageUrl = editingProduct?.image || '';
      }

      // Use existing additional images
      if (formData.additionalImages) {
        additionalImagesUrls.push(...formData.additionalImages);
      }

      // Upload new additional images
      if (additionalImageFiles.length > 0) {
        for (const file of additionalImageFiles) {
          const url = await uploadImage(file);
          additionalImagesUrls.push(url);
        }
      }

      const productData = {
        name: formData.name,
        description: formData.description,
        image_url: imageUrl,
        additional_images: additionalImagesUrls,
        price: Number(formData.price),
        points_required: Number(formData.pointsPrice),
        stock_quantity: Number(formData.stock),
        brand_id: formData.brandId,
        category_id: formData.categoryId,
        subcategory_id: formData.subcategoryId || null,
        is_active: formData.status === 'active'
      };

      if (isEditing && editingProduct) {
        // Update existing product
        const { data, error } = await invokeAdmin('admin-manage-products', {
          action: 'update',
          productId: editingProduct.id,
          productData
        });

        if (error) throw error;
        if (!data?.success) throw new Error(data?.error || 'Erro ao actualizar produto');

        toast({
          title: "Sucesso",
          description: "Produto actualizado com sucesso",
        });
      } else {
        // Create new product
        const { data, error } = await invokeAdmin('admin-manage-products', {
          action: 'create',
          productData
        });

        if (error) throw error;
        if (!data?.success) throw new Error(data?.error || 'Erro ao criar produto');

        toast({
          title: "Sucesso",
          description: "Produto criado com sucesso",
        });
      }

      await onProductSaved();
      onClose();
    } catch (error: any) {
      console.error("Erro ao salvar produto:", error);
      toast({
        title: "Erro",
        description: error.message || "Ocorreu um erro. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-4xl max-h-[95vh] bg-brand-gray border-0 overflow-y-auto p-4 md:p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white">
            {isEditing ? `Editar Produto: ${editingProduct?.name}` : 'Adicionar Novo Produto'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white">Nome*</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Nome do produto"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="product-image-upload" className="text-white">Imagem principal*</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="bg-transparent border-gray-400 text-white hover:bg-gray-600"
                  onClick={() => document.getElementById("product-image-upload")?.click()}
                >
                  <Upload size={16} className="mr-2" />
                  Selecionar Imagem
                </Button>
                <Input
                  id="product-image-upload"
                  type="file"
                  className="hidden"
                  onChange={handleImageUpload}
                  accept="image/png,image/jpeg,image/webp,image/gif"
                />
                {formData.image && (
                  <div className="h-10 w-10 rounded overflow-hidden">
                    <img
                      src={formData.image}
                      alt="Preview"
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="additional-images-upload" className="text-white">Imagens adicionais</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="bg-transparent border-gray-400 text-white hover:bg-gray-600"
                  onClick={() => document.getElementById("additional-images-upload")?.click()}
                >
                  <Upload size={16} className="mr-2" />
                  Carregar Imagens
                </Button>
                <Input
                  id="additional-images-upload"
                  type="file"
                  className="hidden"
                  onChange={handleAdditionalImagesUpload}
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  multiple
                />
                {additionalImageFiles.length > 0 && (
                  <span className="text-sm text-gray-400">
                    {additionalImageFiles.length} imagem(ns) seleccionada(s)
                  </span>
                )}
              </div>

              {/* Show existing additional images in edit mode */}
              {isEditing && formData.additionalImages && formData.additionalImages.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-gray-400 mb-2">Imagens adicionais existentes:</p>
                  <div className="flex flex-wrap gap-2">
                    {formData.additionalImages.map((imageUrl, index) => (
                      <div key={`existing-${index}`} className="relative h-16 w-16 group">
                        <img
                          src={imageUrl}
                          alt={`Imagem adicional existente ${index + 1}`}
                          className="h-full w-full object-cover rounded border-2 border-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => removeExistingAdditionalImage(imageUrl)}
                          className="absolute top-0 right-0 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Show new additional images being added */}
              {additionalImageFiles.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-gray-400 mb-2">Novas imagens a carregar:</p>
                  <div className="flex flex-wrap gap-2">
                    {additionalImageFiles.map((file, index) => (
                      <div key={`new-${index}`} className="relative h-16 w-16 group">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Nova imagem adicional ${index + 1}`}
                          className="h-full w-full object-cover rounded border-2 border-green-500"
                        />
                        <button
                          type="button"
                          onClick={() => removeAdditionalImage(index)}
                          className="absolute top-0 right-0 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="price" className="text-white">Preço (€)</Label>
              <Input
                id="price"
                name="price"
                type="number"
                value={formData.price.toString()}
                onChange={handleInputChange}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pointsPrice" className="text-white">Preço em Pontos</Label>
              <Input
                id="pointsPrice"
                name="pointsPrice"
                type="number"
                value={formData.pointsPrice.toString()}
                onChange={handleInputChange}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="0"
                min="0"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">Marca*</Label>
              <Select
                value={formData.brandId}
                onValueChange={(value) => handleSelectChange("brandId", value)}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue placeholder="Seleccione uma marca" />
                </SelectTrigger>
                <SelectContent>
                  {brands.map(brand => (
                    <SelectItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Categoria*</Label>
              <Select
                value={formData.categoryId}
                onValueChange={(value) => handleSelectChange("categoryId", value)}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue placeholder="Seleccione uma categoria" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(category => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Subcategoria</Label>
              <Select
                value={formData.subcategoryId || ""}
                onValueChange={(value) => handleSelectChange("subcategoryId", value)}
                disabled={!formData.categoryId}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue placeholder={formData.categoryId ? "Seleccione uma subcategoria" : "Seleccione uma categoria primeiro"} />
                </SelectTrigger>
                <SelectContent>
                  {filteredSubcategories.map(subcategory => (
                    <SelectItem key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="stock" className="text-white">Stock</Label>
              <Input
                id="stock"
                name="stock"
                type="number"
                value={formData.stock.toString()}
                onChange={handleInputChange}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="0"
                min="0"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">Status</Label>
              <div className="flex items-center space-x-3">
                <Switch
                  checked={formData.status === 'active'}
                  onCheckedChange={(checked) =>
                    setFormData(prev => ({ ...prev, status: checked ? 'active' : 'inactive' }))
                  }
                />
                <Label className={`text-sm ${formData.status === 'active' ? 'text-green-400' : 'text-red-400'}`}>
                  {formData.status === 'active' ? 'Ativo' : 'Inativo'}
                </Label>
              </div>
            </div>

            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="description" className="text-white">Descrição</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Descreva o produto"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="bg-transparent border-gray-400 text-white hover:bg-gray-600 hover:border-gray-300"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-brand-red hover:bg-brand-red/90 text-white"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center">
                  <span className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-b-transparent"></span>
                  {isEditing ? "A guardar..." : "A criar..."}
                </span>
              ) : (
                isEditing ? "Guardar Alterações" : "Criar Produto"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductModal;